import type { DataSourceDetailsDTO } from '@conversed/rest/src/contracts/data-sources/data-sources.dto';
import {
  DocumentType,
  TrainingStatus,
} from '@conversed/rest/src/contracts/data-sources/data-sources.enums';
import {
  DATA_SOURCES_FOLDER_DEFINITIONS,
  DataSourcesFolderIds,
} from '@conversed/rest/src/contracts/data-sources-folders/data-sources-folders.schema';
import { AUTOMATIC_RETRAINER_ID } from '@conversed/shared/src/central/constants/system-actors';
import { uniqueBy } from '@conversed/shared/src/helpers/data.helpers';
import { mainActions } from '@store/reducers/mainHandler';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useCompanySelector } from '../useCompanySelector';
import { useMainSelector } from '../useMainSelector';
import { useDataSourcesQueryParams } from './useDataSourcesQueryParams';

const TRAIN_FOLDER_NAME =
  DATA_SOURCES_FOLDER_DEFINITIONS[DataSourcesFolderIds.Train].name;

const PRIORITY_FOLDER_NAME =
  DATA_SOURCES_FOLDER_DEFINITIONS[DataSourcesFolderIds.Priority].name;

export const useDataSources = () => {
  const {
    dataSources: {
      files,
      selectedFile,
      search,
      folders = [],
      selectedFolder = null,
    },
  } = useMainSelector();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { subfolderParam } = useDataSourcesQueryParams();
  const { selectedCompany } = useCompanySelector();

  const setDataSourceFile = useCallback(
    (data: DataSourceDetailsDTO) => {
      dispatch(mainActions.setSelectedDataSource(data));
    },
    [dispatch],
  );

  const filteredDataSources = useMemo(() => {
    return files?.filter((file) =>
      file.name?.toLowerCase().includes(search?.toLowerCase()),
    );
  }, [files, search]);

  const dataSources = useMemo(() => {
    const dataSourceFiles = search !== '' ? filteredDataSources : files;

    return uniqueBy(dataSourceFiles, 'key').sort();
  }, [files, filteredDataSources, search]);

  const trainFolderId = useMemo(
    () =>
      folders
        .find((folder) => folder.name === TRAIN_FOLDER_NAME)
        ?._id?.toString(),
    [folders],
  );

  const priorityFolderId = useMemo(
    () =>
      folders
        .find((folder) => folder.name === PRIORITY_FOLDER_NAME)
        ?._id?.toString(),
    [folders],
  );

  const validSelectableFAQDataSources = useMemo(() => {
    return dataSources.filter(
      (file) =>
        file.companyId === selectedCompany?.id &&
        file.trainingStatus &&
        [TrainingStatus.Ready, TrainingStatus.Completed].includes(
          file.trainingStatus,
        ) &&
        file.documentType === DocumentType.FAQ &&
        file.folderPath?.includes(TRAIN_FOLDER_NAME),
    );
  }, [dataSources, selectedCompany]);

  const validSelectablePriorityDataSources = useMemo(() => {
    return dataSources.filter(
      (file) =>
        // Exclude the automated Priority Knowledge file - it stays visible in the folder
        // but is not selectable as an "existing" target in the generation modal.
        file.createdBy !== AUTOMATIC_RETRAINER_ID &&
        file.trainingStatus &&
        [TrainingStatus.Ready, TrainingStatus.Completed].includes(
          file.trainingStatus,
        ) &&
        file.documentType === DocumentType.FAQ &&
        file.folderPath?.includes(PRIORITY_FOLDER_NAME),
    );
  }, [dataSources]);

  const isInTrainFolder = useCallback(
    (folderId?: string | null) => !!folderId && folderId === trainFolderId,
    [trainFolderId],
  );

  const isInPriorityFolder = useCallback(
    (folderId?: string | null) => !!folderId && folderId === priorityFolderId,
    [priorityFolderId],
  );

  const getFolderLabel = useCallback(
    (file: Partial<DataSourceDetailsDTO>) => {
      if (selectedCompany && file.companyId !== selectedCompany.id) {
        return t('data-sources.folder.shared.title');
      }

      if (!file.folderPath?.length) {
        return '-';
      }

      return file.folderPath
        .map((segment) => t(segment, { defaultValue: segment }))
        .join(' / ');
    },
    [t, selectedCompany],
  );

  /**
   * When a folder/subfolder is selected (via Redux + URL params), filter
   * the file list to only show files that belong to that context.
   * - No folder selected → show all files
   * - Folder + subfolder selected → show only files in that specific subfolder
   * - Folder selected, no subfolder → show files in the folder itself and in any of its subfolders
   */
  const folderFilteredDataSources = useMemo(() => {
    if (!selectedFolder) {
      return dataSources;
    }

    // Shared folder: show only files owned by other studios and shared with this one
    if (selectedFolder.name.startsWith(DataSourcesFolderIds.Shared)) {
      if (!selectedCompany) {
        return [];
      }
      return dataSources.filter(
        (dataSource) => dataSource.companyId !== selectedCompany.id,
      );
    }

    if (subfolderParam) {
      const matched = selectedFolder.subfolders?.find(
        (subfolder) => subfolder.name === subfolderParam,
      );
      if (matched) {
        const matchedSubfolderId = matched._id?.toString();
        return dataSources.filter(
          (dataSource) =>
            dataSource.folderId === matchedSubfolderId &&
            dataSource.companyId === selectedCompany?.id, // do not show shared documents from other companies
        );
      }
      return [];
    }

    const folderId = selectedFolder._id?.toString();
    const subfolderIds = new Set(
      (selectedFolder.subfolders || [])
        .map((subfolder) => subfolder._id?.toString())
        .filter(Boolean),
    );

    // Files with no folderId are attributed to the Core folder (same as backend getFolderStats)
    const isCoreFolder = selectedFolder.name.startsWith(
      DataSourcesFolderIds.Core,
    );

    return dataSources.filter(
      (dataSource) =>
        dataSource.companyId === selectedCompany?.id &&
        (dataSource.folderId === folderId ||
          (isCoreFolder && !dataSource.folderId) ||
          (dataSource.folderId && subfolderIds.has(dataSource.folderId))),
    );
  }, [dataSources, selectedFolder, subfolderParam, selectedCompany]);

  return {
    selectedDataSourceFile: selectedFile,
    dataSources,
    folderFilteredDataSources,
    validSelectableFAQDataSources,
    validSelectablePriorityDataSources,
    filteredDataSources,
    setDataSourceFile,
    allFiles: files,
    folders,
    selectedFolder,
    getFolderLabel,
    trainFolderId,
    isInTrainFolder,
    priorityFolderId,
    isInPriorityFolder,
  };
};
